import * as THREE from 'three';
import { TORCH_FX, MELEE_SWING } from '../config';
import { WEAPONS, isWeapon, type WeaponId } from '../game/weapons';
import { getPlayerSkin } from '../game/player';
import { buildWeaponFp, buildToolGlb, buildTorchGlb, torchState, applyHeldFit } from './weaponModels';
import { preloadGLB, cloneGLBIfReady, fitGLBHeld, eachVertexLocal } from './gltf';
import { weaponFit, type V3 } from './weaponFit';

const ZERO3: V3 = [0, 0, 0];

export type HeldItemId = 'hammer' | 'blueprint' | 'rock' | 'ak' | 'chest' | 'bigchest' | 'torch' | 'guidebook'
  | 'bandage' | 'wood_axe' | 'wood_pickaxe' | 'stone_axe' | 'stone_pickaxe' | 'metal_axe' | 'metal_pickaxe' | WeaponId | null;

// First-person viewmodel tunables. Kept here (config.ts is owned elsewhere) so the held-item
// poses, swing arc and AK recoil all read from one block.
export const VIEWMODEL = {
  restPitch: -0.5, // tilt the up-pointing forearm forward into view
  rightArm: { pos: [0.42, -0.58, -0.46] as const, roll: 0.16 }, // more to the right + closer to the camera
  leftArm: { pos: [-0.34, -0.7, -0.55] as const, roll: -0.22, pitchBias: -0.08 },
  // AIMING weapons (guns + bows) point the barrel FORWARD at the crosshair: a near-level arm so the model's -Z
  // (the muzzle) aligns with the camera's forward. Two-handed grips both arms + hides the torch; the pistol is
  // one-handed. Authored so the gun sits lower-centre, muzzle toward the centre dot.
  gunRightArm: { pos: [0.12, -1.12, 0.0] as const, pitch: -0.06, roll: 0.0 },
  gunLeftArm: { pos: [-0.18, -1.12, -0.2] as const, pitch: -0.06, roll: 0.06 },
  pistolArm: { pos: [0.24, -0.72, -0.12] as const, pitch: -0.04, roll: 0.0 },
  gunFlash: [0, 0.96, -0.82] as const,    // two-handed barrel-tip (in right-arm space)
  pistolFlash: [0, 0.64, -0.44] as const, // pistol barrel-tip
  swing: { windup: 0.5, raise: 1.15, slam: 1.5 },
  // Procedural animation layers (added additively onto the base pose each frame; see update()).
  anim: {
    recoilK: 210, recoilC: 22, recoilImpulse: 7,       // recoil = a damped spring impulsed on fire()
    recoilBack: 0.13, recoilUp: 0.05, recoilPitch: 0.2, // how the recoil scalar maps to back/up/muzzle-rise
    swayAmount: 1.6, swayMax: 0.07, swayLerp: 9,        // weapon lags mouse-look (trails a flick, then recovers)
    bobFreq: 9, bobAmp: 0.02,                           // walk/idle bob (figure-8), scaled by move speed
    adsLerp: 13, flashMs: 55,                           // aim-down-sights ease + muzzle-flash duration
  },
} as const;

// Per-frame animation inputs from the game loop (dt + look/move state) so the viewmodel can layer sway/bob/recoil.
export interface VmCtx {
  dt: number;
  swingPhase: number; // melee swing 0..1, <0 idle
  lookDX: number;     // camera yaw delta this frame (rad) -> sway
  moveSpeed: number;  // 0..1 horizontal move speed -> bob
  ads: boolean;       // aiming down sights (right-held on a gun/bow)
  reload: number;     // reload progress 0..1, or <0 when not reloading
  draw01: number;     // bow string draw / spear throw wind-up, 0..1 (0 = idle)
}

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

// VIEWMODEL_LAYER: the held weapon/arms render in a SEPARATE pass (main.ts, right after grade.render())
// with its own tiny near-plane and a freshly-cleared depth buffer, so the gun can never clip into the
// camera's near plane (which reads as seeing THROUGH it to the world/void behind) and never gets occluded
// by world geometry the player is standing close to. Every viewmodel object must carry this layer, since
// three.js does not propagate a parent's layers to its children automatically.
export const VIEWMODEL_LAYER = 1;
function setLayerRecursive(obj: THREE.Object3D, layer: number): void {
  obj.layers.set(layer);
  obj.traverse((o) => o.layers.set(layer));
}

// First-person viewmodel: the local player's arms holding the active item (right) and a torch
// (left), parented to the camera so they rise from the bottom corners of the view. The right arm
// replays the gather swing (raise then slam). Shown only in first person; other clients still see
// the full third-person body. setActiveItem swaps the right-hand model; the AK is two-handed and
// hides the torch.
export class Viewmodel {
  readonly group = new THREE.Group();
  private readonly rightArm = new THREE.Group();
  private readonly leftArm = new THREE.Group();
  private readonly restPitch = VIEWMODEL.restPitch;

  // The active held model lives in this socket at the top of the forearm (the hand). Swapping an
  // item disposes the old model and drops a new one in.
  private readonly itemSocket = new THREE.Group();
  private readonly elbow = new THREE.Group(); // forearm pivot -- bends during melee swings, 0 for guns/tools
  private activeItem: HeldItemId = 'rock';
  private isGun = false;       // current item recoils + flashes on fire() (the AK + every gun)
  private twoHanded = false;   // both arms grip it + the torch hides (rifles/shotguns/bow/launcher)
  private aims = false;        // guns + bows: the muzzle-flash sits at the barrel tip (a real bullet spawn point)
  private swingVariant = 0;    // melee: which swing arc (0 chop, 1 R->L slash, 2 L->R slash, 3 stab)
  private swingCount = 0;      // sword/mace swing counter: alternates slashes, every 3rd is the finisher
  private prevSwingPhase = -1; // to detect a fresh swing (phase rising from idle)
  private gunBase: { pos: readonly number[]; pitch: number; roll: number } = { pos: VIEWMODEL.gunRightArm.pos, pitch: VIEWMODEL.gunRightArm.pitch, roll: VIEWMODEL.gunRightArm.roll };

  // Procedural animation state (additive layers). recoil is a damped spring; sway trails mouse-look; bob is a
  // walk/idle cycle; adsT eases 0..1 toward aim-down-sights.
  private recoil = 0; private recoilVel = 0;
  private swayX = 0; private bobX = 0; private bobY = 0; private bobPhase = 0; private adsT = 0;
  private adsOff: V3 = [0, 0, 0]; // per-gun ADS position delta on top of the auto solve (weaponFit.ads, F8)
  private adsRotOff: V3 = [0, 0, 0]; // per-gun ADS rotation delta, independent of the hip `rot` (weaponFit.adsRot, F8)
  // EXACT ADS pose, solved per-equip from the model's real geometry (see solveAim in setActiveItem):
  // at full aim the arm is rotated square to the camera (barrel parallel to the view axis) and moved so the
  // gun's SIGHT LINE sits on the camera's centre axis -- the bullet (which converges on the crosshair) then
  // lands exactly where the sights point, for any gun, automatically. The rear of the gun is pushed forward
  // past the camera by a fixed clearance so the receiver can never swallow the lens (the failure that killed
  // the 2026-07-09 attempt).
  private adsPos: V3 = [0, 0, 0];
  private bowString: THREE.Line | null = null; // GLB bow dressing (weaponModels.dressBow): animated by draw01
  private bowArrow: THREE.Object3D | null = null;
  private muzzleDebug = false;    // F8 gun-fit overlay: keep the flash lit so the muzzle point is visible

  private readonly muzzleFlash: THREE.Sprite;
  private fireAt = -1; // performance.now() of the last shot, < 0 when idle

  // First-person torch flame: scaled up over TORCH_FX.igniteSecs on equip, then flickers forever.
  private torchFlame: THREE.Object3D | null = null;
  private torchT = 0; private torchIgnite = 1;

  constructor() {
    // Match the FIRST-person arms to the player's chosen skin tone (same source buildHumanoid uses for the
    // 3rd-person body), so your own hands are the colour you picked on the menu, not a hardcoded tan.
    const skin = new THREE.MeshLambertMaterial({ color: getPlayerSkin().skin, flatShading: true });
    const armGeo = new THREE.BoxGeometry(0.12, 0.42, 0.14);

    // Right arm with a real ELBOW (owner 2026-07-12: weighted melee swings): an upper-arm segment from
    // the shoulder pivot, then a forearm group pivoting at the elbow. The hand socket rides the forearm,
    // so bending the elbow cocks/extends the weapon naturally instead of the whole arm swinging as a
    // plank. Elbow bend 0 = the exact silhouette the old one-piece arm had (guns/tools unaffected).
    const upperGeo = new THREE.BoxGeometry(0.12, 0.24, 0.14);
    const foreGeo = new THREE.BoxGeometry(0.11, 0.25, 0.13);
    const rUpper = new THREE.Mesh(upperGeo, skin);
    rUpper.position.set(0, 0.11, 0);
    this.elbow.position.set(0, 0.22, 0);          // elbow pivot at the top of the upper arm
    const rFore = new THREE.Mesh(foreGeo, skin);
    rFore.position.set(0, 0.115, 0);
    this.itemSocket.position.set(0, 0.23, 0);     // the hand, at the top of the forearm (0.22 + 0.23 = 0.45 total, same reach)
    this.elbow.add(rFore, this.itemSocket);
    this.rightArm.add(rUpper, this.elbow);
    this.rightArm.position.set(...VIEWMODEL.rightArm.pos);
    this.rightArm.rotation.set(this.restPitch, 0, VIEWMODEL.rightArm.roll);
    this.group.add(this.rightArm);

    // Left arm (just the hand now -- the torch is a wielded item, shown in the RIGHT hand only when out).
    const lLimb = new THREE.Mesh(armGeo, skin);
    lLimb.position.set(0, 0.21, 0);
    this.leftArm.add(lLimb);
    this.leftArm.position.set(...VIEWMODEL.leftArm.pos);
    this.leftArm.rotation.set(this.restPitch + VIEWMODEL.leftArm.pitchBias, 0, VIEWMODEL.leftArm.roll);
    this.group.add(this.leftArm);

    // Muzzle flash sprite, parented to the right arm so it tracks the gun. Hidden until fire().
    this.muzzleFlash = makeMuzzleFlash();
    this.muzzleFlash.visible = false;
    this.rightArm.add(this.muzzleFlash);

    // Default held model.
    this.setActiveItem('rock');

    this.group.visible = false;
    this.group.renderOrder = 2; // drawn after the world
    setLayerRecursive(this.group, VIEWMODEL_LAYER); // arms + muzzle flash; setActiveItem tags each swapped-in model too
  }

  setVisible(v: boolean): void {
    this.group.visible = v;
  }

  // Swap the first-person held model. The AK is two-handed: it reposes both arms onto the gun and
  // hides the torch; everything else keeps the one-handed pose with the torch in the left hand.
  setActiveItem(id: HeldItemId): void {
    if (id === this.activeItem && this.itemSocket.children.length > 0) return;
    this.activeItem = id;
    disposeChildren(this.itemSocket);

    const model = id ? buildHeldModel(id) : null;
    if (model) { setLayerRecursive(model, VIEWMODEL_LAYER); this.itemSocket.add(model); }
    // Torch equip: catch the flame from a spark (scale ramp in update()) instead of popping in full-size.
    this.torchFlame = id === 'torch' && model ? model.getObjectByName('torchFlame') ?? null : null;
    if (this.torchFlame) { this.torchIgnite = 0; this.torchT = 0; this.torchFlame.scale.setScalar(0.02); }

    const weapon = isWeapon(id) ? WEAPONS[id] : null;
    this.twoHanded = id === 'ak' || (weapon?.twoHanded ?? false);
    this.isGun = id === 'ak' || weapon?.kind === 'gun';
    const aims = this.isGun || weapon?.kind === 'bow'; // guns + bows hold forward, barrel toward the crosshair
    this.aims = aims;
    this.leftArm.visible = this.twoHanded; // left arm ONLY for two-handed weapons; everything else is right-hand only
    this.muzzleFlash.visible = false;
    this.fireAt = -1;

    if (aims && this.twoHanded) {
      // Both arms grip a forward-pointing long gun / bow; muzzle flash at the barrel tip.
      this.rightArm.position.set(...VIEWMODEL.gunRightArm.pos);
      this.rightArm.rotation.set(VIEWMODEL.gunRightArm.pitch, 0, VIEWMODEL.gunRightArm.roll);
      this.leftArm.position.set(...VIEWMODEL.gunLeftArm.pos);
      this.leftArm.rotation.set(VIEWMODEL.gunLeftArm.pitch, 0, VIEWMODEL.gunLeftArm.roll);
      this.muzzleFlash.position.set(...VIEWMODEL.gunFlash);
      this.gunBase = { pos: VIEWMODEL.gunRightArm.pos, pitch: VIEWMODEL.gunRightArm.pitch, roll: VIEWMODEL.gunRightArm.roll };
    } else if (aims) {
      // One-handed pistol, held forward.
      this.rightArm.position.set(...VIEWMODEL.pistolArm.pos);
      this.rightArm.rotation.set(VIEWMODEL.pistolArm.pitch, 0, VIEWMODEL.pistolArm.roll);
      this.muzzleFlash.position.set(...VIEWMODEL.pistolFlash);
      this.gunBase = { pos: VIEWMODEL.pistolArm.pos, pitch: VIEWMODEL.pistolArm.pitch, roll: VIEWMODEL.pistolArm.roll };
    } else {
      // Melee / throw / place / tools: the swing (rest) pose, held up to strike.
      this.rightArm.position.set(...VIEWMODEL.rightArm.pos);
      this.rightArm.rotation.set(this.restPitch, 0, VIEWMODEL.rightArm.roll);
      this.gunBase = { pos: VIEWMODEL.rightArm.pos, pitch: this.restPitch, roll: VIEWMODEL.rightArm.roll };
    }
    // AUTO-FIT from the model's real geometry (owner ask: "matched for all guns automatically"): the muzzle
    // point is the centroid of the front 8% of the gun (the barrel tip), and the ADS pose is solved so the
    // sight line lands on the camera axis. Hand-tuned weaponFit muzzle/ads (F8) remain as overrides/trim.
    this.bowString = model?.getObjectByName('bowString') as THREE.Line | null ?? null;
    this.bowArrow = model?.getObjectByName('bowArrow') ?? null;
    if (aims && model) this.solveAim(model, id === 'bow');
    const fit = weaponFit(id);
    if (fit.muzzle) this.muzzleFlash.position.set(fit.muzzle[0], fit.muzzle[1], fit.muzzle[2]);
    this.adsOff = fit.ads ?? ZERO3;
    this.adsRotOff = fit.adsRot ?? ZERO3;
    this.recoil = 0; this.recoilVel = 0; this.adsT = 0; // reset transient anim state on swap
  }

  // Solve the EXACT aim fit from the held model's real vertices, in RIGHT-ARM space (model local + the
  // 0.45 hand-socket rise). Two outputs:
  //  - muzzle point: centroid of the front 8% slab of the gun = the barrel tip. The flash sprite AND the
  //    tracer origin (muzzleWorld) sit here, so shots visibly leave the real barrel of every gun.
  //  - adsPos: the arm position at FULL aim. At full ADS update() also levels the arm square to the camera,
  //    so the gun's -Z is parallel to the view axis; adsPos then just has to put the SIGHT LINE (top of the
  //    gun; for the bow, the nocked arrow's line) on the camera centre: x = -sightX, y = -sightTop - a hair
  //    (the post tip kisses the aim point from below). z pushes the whole gun forward until its rearmost
  //    vertex clears the camera by 0.14 -- the receiver can never swallow the lens, no matter the model.
  private solveAim(model: THREE.Object3D, isBow: boolean): void {
    const box = new THREE.Box3(); box.makeEmpty();
    eachVertexLocal(model, (v) => box.expandByPoint(v));
    if (box.isEmpty()) { this.adsPos = [this.gunBase.pos[0] * 0.05, this.gunBase.pos[1] + 0.07, this.gunBase.pos[2] - 0.08]; return; }
    const len = box.max.z - box.min.z || 1;
    const slab = box.min.z + len * 0.08;
    const c = new THREE.Vector3(); let n = 0;
    eachVertexLocal(model, (v) => { if (v.z < slab) { c.add(v); n++; } });
    if (n) c.divideScalar(n);
    const SOCKET_Y = 0.45; // itemSocket rise within the arm
    const fit = weaponFit(this.activeItem);
    if (!fit.muzzle) this.muzzleFlash.position.set(c.x, c.y + SOCKET_Y, box.min.z - 0.01);
    let sightX = c.x, sightTop = box.max.y + SOCKET_Y, rear = box.max.z;
    if (isBow && this.bowString) {
      // The bow aims down its ARROW, not over its limb tips: centre the nock line instead of the bbox top.
      const tips = this.bowString.userData.tips as { rest: number[]; pull: number };
      sightX = tips.rest[0]; sightTop = tips.rest[1] + SOCKET_Y; rear = tips.rest[2] + tips.pull + 0.1; // drawn arrow tail
      if (!fit.muzzle) this.muzzleFlash.position.set(tips.rest[0], tips.rest[1] + SOCKET_Y, box.min.z - 0.01); // arrows leave on the aim line
    }
    this.adsPos = [-sightX, -0.008 - sightTop, -0.14 - rear];
  }

  // F8 gun-fit overlay: pin the muzzle flash ON so the muzzle point reads while tuning (off = normal flashes).
  setMuzzleDebug(on: boolean): void {
    this.muzzleDebug = on;
    if (!on) this.muzzleFlash.visible = false;
  }

  // Rebuild the current held model + pose (bypasses the same-id guard) -- the F4 Guns panel calls this so
  // slider edits show on the equipped weapon immediately.
  reequip(): void {
    const id = this.activeItem;
    this.activeItem = null;
    if (id) this.setActiveItem(id);
  }

  // Gun fire hook: impulse the recoil spring (kick scales the punch) + pop the muzzle flash. No-op unless a gun
  // (the AK or any firearm) is the active item.
  fire(kick = 1): void {
    if (!this.isGun) return;
    this.recoilVel += kick * VIEWMODEL.anim.recoilImpulse;
    this.fireAt = performance.now();
    this.muzzleFlash.visible = true;
    this.muzzleFlash.material.rotation = Math.random() * Math.PI; // spin the flash so repeats differ
  }

  // World position of the RENDERED barrel tip (the muzzle-flash socket), so bullets/arrows leave the actual gun
  // in first person instead of the camera/head. Returns false for non-aiming items (use a fallback offset then).
  muzzleWorld(out: THREE.Vector3): boolean {
    if (!this.aims || !this.group.visible) return false;
    this.muzzleFlash.updateWorldMatrix(true, false); // refresh the arm+camera chain so the world pos is current
    this.muzzleFlash.getWorldPosition(out);
    return true;
  }

  // Per-frame animation. Ticks the procedural layers (recoil spring, sway, bob, ADS ease) then composes the pose:
  // reload (multi-phase) > gun/bow (base + recoil + sway + bob, eased toward ADS) > melee/tools (swing + idle bob).
  update(ctx: VmCtx): void {
    const dt = Math.max(0, Math.min(0.05, ctx.dt)); // clamp BOTH ways: a stalled frame can't blow up the springs, and a
    // stray negative delta (a frame-timer hiccup) can't invert the exp(-k*dt) decay curves below into a blend factor
    // outside 0..1 -- that flips every ease (recoil/sway/ads) from "converge" to "diverge", a real bug independent of
    // any one caller.
    this.tickRecoil(dt);
    this.tickSway(dt, ctx.lookDX);
    this.tickBob(dt, ctx.moveSpeed);
    this.adsT += ((ctx.ads ? 1 : 0) - this.adsT) * (1 - Math.exp(-VIEWMODEL.anim.adsLerp * dt));
    this.tickTorchFlame(dt);
    const b = this.gunBase, A = VIEWMODEL.anim;

    if (ctx.reload >= 0) {
      // Multi-phase reload: dip out of the aim line (0-0.15), hold with a mag-swap tug (mid), raise back (0.85-1).
      const r = ctx.reload;
      const dip = r < 0.15 ? r / 0.15 : r > 0.85 ? (1 - r) / 0.15 : 1;
      const tug = (r > 0.25 && r < 0.75) ? Math.sin(r * Math.PI * 4) * 0.06 : 0;
      this.rightArm.position.set(b.pos[0] + 0.05, b.pos[1] - 0.26 * dip, b.pos[2] + 0.05 * dip);
      this.rightArm.rotation.set(b.pitch - 0.5 * dip + tug, 0.12 * dip, b.roll + 0.3 * dip);
      this.elbow.rotation.x = 0;
      this.muzzleFlash.visible = false;
      if (this.twoHanded) { this.leftArm.position.set(VIEWMODEL.gunLeftArm.pos[0], VIEWMODEL.gunLeftArm.pos[1] - 0.22 * dip, VIEWMODEL.gunLeftArm.pos[2]); this.leftArm.rotation.set(VIEWMODEL.gunLeftArm.pitch - 0.35 * dip, 0, VIEWMODEL.gunLeftArm.roll); }
      return;
    }

    if (this.isGun || this.aims) {
      if (this.muzzleFlash.visible && !this.muzzleDebug && performance.now() - this.fireAt > A.flashMs) this.muzzleFlash.visible = false;
      if (this.muzzleDebug && !this.muzzleFlash.visible) this.muzzleFlash.visible = true;
      const ads = this.adsT, damp = 1 - ads * 0.8; // ADS steadies sway/bob
      // EXACT ADS (2026-07-11, replaces both the hand-tuned offsets and the reverted 2026-07-09 adsCenterXY
      // solve): lerp toward the per-gun solved pose (solveAim) while the arm LEVELS square to the camera --
      // at full aim the barrel is parallel to the view axis and the sight line sits on the crosshair, so the
      // bullet lands exactly where the sights point. The solve pushes the gun's rearmost vertex a fixed
      // clearance past the camera, which is what the old attempt was missing (the receiver ate the lens).
      this.elbow.rotation.x = 0; // straight arm for guns/bows: the aim solve owns the geometry
      const px0 = lerp(b.pos[0], this.adsPos[0] + this.adsOff[0], ads), py0 = lerp(b.pos[1], this.adsPos[1] + this.adsOff[1], ads), pz0 = lerp(b.pos[2], this.adsPos[2] + this.adsOff[2], ads);
      this.rightArm.position.set(px0 + (this.swayX + this.bobX) * damp, py0 + this.bobY * damp + this.recoil * A.recoilUp, pz0 + this.recoil * A.recoilBack);
      this.rightArm.rotation.set(lerp(b.pitch, this.adsRotOff[0], ads) - this.recoil * A.recoilPitch, lerp(0, this.adsRotOff[1], ads) + this.swayX * 1.6 * damp, lerp(b.roll, this.adsRotOff[2], ads) + this.swayX * 2.2 * damp);
      // Bow: the string's middle point + the nocked arrow slide back with the draw (draw01 from main's charge state).
      if (this.bowString) {
        const tips = this.bowString.userData.tips as { rest: number[]; pull: number };
        const nz = tips.rest[2] + tips.pull * ctx.draw01;
        const posAttr = this.bowString.geometry.getAttribute('position') as THREE.BufferAttribute;
        posAttr.setXYZ(1, tips.rest[0], tips.rest[1], nz);
        posAttr.needsUpdate = true;
        if (this.bowArrow) { this.bowArrow.visible = ctx.draw01 > 0; this.bowArrow.position.set(tips.rest[0], tips.rest[1], nz); }
      }
      if (this.twoHanded) { this.leftArm.position.set(VIEWMODEL.gunLeftArm.pos[0] + this.swayX * damp, VIEWMODEL.gunLeftArm.pos[1] + this.bobY * damp, VIEWMODEL.gunLeftArm.pos[2]); this.leftArm.rotation.set(VIEWMODEL.gunLeftArm.pitch, 0, VIEWMODEL.gunLeftArm.roll); }
      return;
    }

    // MELEE (owner 2026-07-12): a WEIGHTED three-phase swing -- anticipation (cock up-back, elbow bends),
    // STRIKE (accelerating cut DOWN-AND-FORWARD, elbow extends, cubic easing so the mass reads), then a
    // long heavy RECOVERY (the weapon hangs buried a beat, then draws slowly back to rest). ctx.swingPhase
    // runs 0..1 over strike+recover (main sizes the swing from MELEE_SWING per weapon); spears stab.
    const sp = ctx.swingPhase;
    if (sp >= 0 && this.prevSwingPhase < 0) {
      const stabber = this.activeItem === 'spear' || this.activeItem === 'stone_spear';
      if (this.activeItem === 'sword' || this.activeItem === 'mace') { this.swingCount++; this.swingVariant = this.swingCount % 2 === 0 ? 2 : 1; } // alternate cut lanes
      else this.swingVariant = stabber ? 3 : 0;
    }
    this.prevSwingPhase = sp;
    const rest = VIEWMODEL.rightArm;
    const sw = MELEE_SWING[this.activeItem ?? ''] ?? MELEE_SWING.default;
    const fs = sw.strike / (sw.strike + sw.recover); // strike fraction of the whole animation
    let pitch: number = this.restPitch, roll: number = rest.roll, yaw = 0, elbowX = 0;
    let xoff: number = rest.pos[0], yoff: number = rest.pos[1], zoff: number = rest.pos[2];
    if (sp >= 0) {
      const lane = this.swingVariant === 2 ? -1 : this.swingVariant === 1 ? 1 : 0; // alternating slight L/R lanes
      if (this.swingVariant === 3) {
        // SPEAR STAB: pull back + elbow cock, explosive lunge, medium ease back.
        if (sp < fs) {
          const u = sp / fs;
          const back = Math.min(1, u / 0.45), lungeT = Math.max(0, (u - 0.45) / 0.55);
          const l = lungeT * lungeT;
          pitch = this.restPitch + 0.42;
          elbowX = 0.8 * back * (1 - l); // FLEX (positive = forearm folds toward the bicep, like a real elbow)
          zoff = rest.pos[2] + 0.16 * back - 0.72 * l;
          yoff = rest.pos[1] - 0.05 * l;
        } else {
          const r = (sp - fs) / (1 - fs), e = r * r * (3 - 2 * r);
          pitch = lerp(this.restPitch + 0.42, this.restPitch, e);
          zoff = lerp(rest.pos[2] - 0.56, rest.pos[2], e);
          yoff = lerp(rest.pos[1] - 0.05, rest.pos[1], e);
        }
      } else if (sp < fs) {
        // STRIKE: anticipation (first 40% -- raise up-back, elbow bends, weapon cocked over the shoulder),
        // then the CUT (60% -- cubic acceleration down through the centre with a forward lunge).
        const u = sp / fs;
        if (u < 0.4) {
          const t = u / 0.4, e = 1 - (1 - t) * (1 - t); // fast raise that settles at the top
          pitch = this.restPitch - 1.35 * e;            // cocked up and back
          elbowX = 1.1 * e;                             // elbow FLEXES (forearm folds toward the bicep) -- the head hangs behind the shoulder
          xoff = rest.pos[0] + lane * 0.16 * e;
          yoff = rest.pos[1] + 0.16 * e;
          roll = rest.roll + lane * 0.35 * e;
          yaw = lane * 0.18 * e;
        } else {
          const t = (u - 0.4) / 0.6, e = t * t * t;     // accelerating mass -- the weight of the cut
          pitch = lerp(this.restPitch - 1.35, this.restPitch + 1.55, e); // DOWN and THROUGH
          elbowX = lerp(1.1, 0, e);                     // elbow whips to full extension (0 = straight; a real elbow never bends past it)
          xoff = rest.pos[0] + lane * (0.16 - 0.3 * e);
          yoff = rest.pos[1] + 0.16 - 0.42 * e;         // drops with the blow
          zoff = rest.pos[2] - 0.5 * e;                 // FORWARD lunge into the target
          roll = rest.roll + lane * (0.35 - 0.75 * e);
          yaw = lane * (0.18 - 0.42 * e);
        }
      } else {
        // RECOVERY: the weapon hangs buried for the first beat, then draws back up slowly (heavy).
        const r = (sp - fs) / (1 - fs);
        const rr = Math.max(0, (r - 0.14) / 0.86);
        const e = Math.pow(rr, 1.6); // hold at the bottom, then a slow weighted rise
        pitch = lerp(this.restPitch + 1.55, this.restPitch, e);
        elbowX = lerp(0.3, 0, e); // impact rebound: a slight flex as the blow lands, easing straight
        xoff = lerp(rest.pos[0] + lane * -0.14, rest.pos[0], e);
        yoff = lerp(rest.pos[1] - 0.26, rest.pos[1], e);
        zoff = lerp(rest.pos[2] - 0.5, rest.pos[2], e);
        roll = lerp(rest.roll + lane * -0.4, rest.roll, e);
        yaw = lerp(lane * -0.24, 0, e);
      }
    } else if (ctx.draw01 > 0) { // spear throw wind-up (owner fix): the arm levels FORWARD so the spear
      // points straight out at the target (tip toward the crosshair), not cocked back over the shoulder.
      const w = ctx.draw01;
      pitch = lerp(this.restPitch, -1.35, w); // arm swings up-and-forward until the spear lies level
      xoff = lerp(rest.pos[0], 0.2, w);       // drift toward centre so the tip reads on the aim point
      zoff = lerp(rest.pos[2], -0.3, w);
      elbowX = 0.35 * w;                      // a light FLEX so the cocked throw reads alive
    } else { xoff += this.bobX * 0.6; zoff += this.bobY * 0.4; pitch += this.bobY * 0.3; } // idle bob for tools/melee
    this.rightArm.rotation.set(pitch, yaw, roll);
    this.rightArm.position.set(xoff, yoff, zoff);
    elbowX = Math.max(0, Math.min(2.6, elbowX)); // anatomy clamp: 0 = straight, flexion only -- an elbow NEVER hyperextends
    this.elbow.rotation.x += (elbowX - this.elbow.rotation.x) * Math.min(1, ctx.dt * 30); // tiny smoothing so the joint never pops
  }

  // Damped-spring recoil, substepped so it's stable even at a low/uneven frame rate.
  private tickRecoil(dt: number): void {
    const A = VIEWMODEL.anim; let t = dt;
    while (t > 0) { const s = Math.min(0.016, t); t -= s; this.recoilVel += (-A.recoilK * this.recoil - A.recoilC * this.recoilVel) * s; this.recoil += this.recoilVel * s; }
    if (Math.abs(this.recoil) < 1e-4 && Math.abs(this.recoilVel) < 1e-3) { this.recoil = 0; this.recoilVel = 0; }
  }

  // Weapon lags mouse-look: ease toward a clamped offset opposite the yaw delta, recovering to 0 when still.
  private tickSway(dt: number, lookDX: number): void {
    const A = VIEWMODEL.anim;
    const tx = Math.max(-A.swayMax, Math.min(A.swayMax, -lookDX * A.swayAmount));
    this.swayX += (tx - this.swayX) * (1 - Math.exp(-A.swayLerp * dt));
  }

  // Torch flame: ignition scale-up (smoothstepped) with a taller-than-wide wobble, then a subtle
  // two-sine flicker forever. Mirrors Player.tickTorch (which drives the light + 3rd-person flame).
  private tickTorchFlame(dt: number): void {
    if (this.activeItem === 'torch') (this.itemSocket.getObjectByName('torchFx')?.userData as { tick?: (dt: number, lit: number) => void } | undefined)?.tick?.(dt, torchState.lit01);
    if (!this.torchFlame) return;
    this.torchT += dt;
    this.torchIgnite = Math.min(1, this.torchIgnite + dt / TORCH_FX.igniteSecs);
    const ig = this.torchIgnite * this.torchIgnite * (3 - 2 * this.torchIgnite);
    const w = TORCH_FX.flameFlicker * (0.6 * Math.sin(this.torchT * 23) + 0.4 * Math.sin(this.torchT * 9.7));
    const s = Math.max(0.02, ig * torchState.lit01 * (1 + w)); // OFF by default: the flame only exists while lit
    this.torchFlame.scale.set(s, Math.max(0.02, ig * (1 + w * 1.8)), s); // height flickers more: flames lick upward
  }

  // Walk/idle bob: a figure-8 that grows with move speed (a subtle breathing sway when standing still).
  private tickBob(dt: number, moveSpeed: number): void {
    const A = VIEWMODEL.anim;
    this.bobPhase += dt * A.bobFreq * (0.4 + moveSpeed);
    const amp = A.bobAmp * (0.3 + moveSpeed * 0.9);
    this.bobX = Math.sin(this.bobPhase) * amp;
    this.bobY = Math.sin(this.bobPhase * 2) * amp * 0.6;
  }
}

// Build the right-hand held model for an item, centered so it sits naturally in the hand socket
// (origin at the grip). Low-poly, flat-shaded to match the world.
// EVERY non-weapon handheld gets the F8 fit applied HERE, once, centrally (owner: F8 must affect every
// item in the hand -- guidebook, bandage, chests, all of it). Weapons apply theirs inside buildWeaponFp.
function buildHeldModel(id: Exclude<HeldItemId, null>): THREE.Group {
  if (isWeapon(id)) return buildWeaponFp(id);
  const built = buildHeldModelInner(id);
  applyHeldFit(built, id, false);
  return built;
}
function buildHeldModelInner(id: Exclude<HeldItemId, null>): THREE.Group {
  switch (id) {
    case 'rock': return buildRock();
    case 'hammer': return buildToolGlb('hammer', buildHammer);
    case 'blueprint': return buildBlueprint();
    case 'chest': return buildHeldChest(0.13);
    case 'bigchest': return buildHeldChest(0.17);
    case 'torch': return buildTorchVm();
    case 'wood_axe': return buildToolGlb('wood_axe', () => buildToolVm('axe', 0x8a6a3a));
    case 'stone_axe': return buildToolGlb('stone_axe', () => buildToolVm('axe', 0x8f9298));
    case 'metal_axe': return buildToolGlb('metal_axe', () => buildToolVm('axe', 0xc2c7cd));
    case 'wood_pickaxe': return buildToolGlb('wood_pickaxe', () => buildToolVm('pickaxe', 0x7a5e34));
    case 'stone_pickaxe': return buildToolGlb('stone_pickaxe', () => buildToolVm('pickaxe', 0x82868c));
    case 'metal_pickaxe': return buildToolGlb('metal_pickaxe', () => buildToolVm('pickaxe', 0xb4b9c0));
    case 'bandage': return buildBandage();
  }
  return new THREE.Group(); // unreachable: weapon ids handled above, the rest are covered by the switch
}


// First-person bandage: a small rolled gauze cylinder with a red cross so it reads as a medical item in-hand.
function buildBandage(): THREE.Group {
  const g = new THREE.Group();
  const gauze = new THREE.MeshLambertMaterial({ color: 0xeae3d5, flatShading: true });
  const cross = new THREE.MeshBasicMaterial({ color: 0xc4392f });
  const roll = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.09, 12), gauze);
  roll.rotation.z = Math.PI / 2; // roll axis points left-right so the flat face aims at the camera
  roll.position.set(0, 0.02, -0.04);
  const barV = new THREE.Mesh(new THREE.BoxGeometry(0.018, 0.07, 0.006), cross);
  const barH = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.018, 0.006), cross);
  // Face the cross out the roll's flat end (+X after the z-rotation), just proud of the gauze surface.
  for (const b of [barV, barH]) { b.rotation.y = Math.PI / 2; b.position.set(0.046, 0.02, -0.04); g.add(b); }
  g.add(roll);
  return g;
}

// First-person axe / pickaxe (tier-coloured head).
function buildToolVm(kind: 'axe' | 'pickaxe', headColor: number): THREE.Group {
  const g = new THREE.Group();
  const wood = new THREE.MeshLambertMaterial({ color: 0x6b4a2b, flatShading: true });
  const head = new THREE.MeshLambertMaterial({ color: headColor, flatShading: true });
  const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.03, 0.42, 6), wood);
  handle.position.y = 0.1;
  if (kind === 'axe') {
    const blade = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.13, 0.16), head);
    blade.position.set(0, 0.3, -0.08); // -Z = forward in first person (camera looks down -Z), so the bit leads
    g.add(handle, blade);
  } else {
    // Pickaxe head runs FORE-AFT (along the swing direction), NOT side to side, with the pick leading forward.
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.05, 0.36), head);
    bar.position.set(0, 0.3, -0.06);
    bar.rotation.x = 0.25; // tip dips forward like a real pick
    g.add(handle, bar);
  }
  return g;
}

// First-person torch: the owner's on/off GLB with the shared fire+smoke rig (weaponModels.buildTorchGlb);
// the old stick+cone stays as the cold-load fallback. Toggling re-equips, so the model always matches lit.
function buildTorchVm(): THREE.Group {
  const glb = buildTorchGlb();
  if (glb) return glb;
  const g = new THREE.Group();
  const stick = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.04, 0.5, 5), new THREE.MeshLambertMaterial({ color: 0x5a3b22, flatShading: true }));
  stick.position.y = 0.18;
  // Flame anchored at its BASE so the ignition scale-up grows out of the stick tip (see Viewmodel.tickTorchFlame).
  const flame = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.28, 7).translate(0, 0.14, 0), new THREE.MeshBasicMaterial({ color: 0xffb347 }));
  flame.position.y = 0.36;
  flame.name = 'torchFlame';
  g.add(stick, flame);
  return g;
}

// A little chest held in hand before deploying it (scaled down to viewmodel size).
function buildHeldChest(size: number): THREE.Group {
  const g = new THREE.Group();
  const wood = new THREE.MeshLambertMaterial({ color: 0x7a5230, flatShading: true });
  const lid = new THREE.MeshLambertMaterial({ color: 0x8a5f37, flatShading: true });
  const body = new THREE.Mesh(new THREE.BoxGeometry(size * 1.4, size, size), wood);
  body.position.y = 0.04;
  const top = new THREE.Mesh(new THREE.BoxGeometry(size * 1.4, size * 0.5, size), lid);
  top.position.y = 0.04 + size * 0.72;
  g.add(body, top);
  return g;
}

// Owner-modelled GLB (2026-07-10) replacing the procedural rock below (kept as the instant-load fallback).
// 'rock' isn't a real WeaponId, so it bypasses weaponModels.ts's WEAPON_GLB map -- same synchronous
// cloneGLBIfReady check (no async swap-in-place: re-equipping picks up the GLB once it's warm, same as guns).
const ROCK_FILE = 'rock.glb';
preloadGLB('/models/' + ROCK_FILE);
function buildRock(): THREE.Group {
  const g = new THREE.Group();
  const ready = cloneGLBIfReady('/models/' + ROCK_FILE);
  if (ready) {
    const fitted = fitGLBHeld(ready, ROCK_FILE, 1);
    // Pitch the rock's top toward the camera so its flat BOTTOM face aims down/away (owner: you should
    // never see the underside sitting up out of the hand -- the rock faces the player view).
    fitted.rotation.x = 0.55;
    g.add(fitted);
    return g;
  }
  const rock = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.12, 0),
    new THREE.MeshLambertMaterial({ color: 0x8a8f96, flatShading: true }),
  );
  g.add(rock);
  return g;
}

// Matched to the owner's hammer.png icon: a heavy SLEDGE -- big grey block head bound in dark straps,
// wood handle with a dark lashing under the head (the old model was a small claw hammer).
function buildHammer(): THREE.Group {
  const g = new THREE.Group();
  const wood = new THREE.MeshLambertMaterial({ color: 0x7a5230, flatShading: true });
  const metal = new THREE.MeshLambertMaterial({ color: 0x878d94, flatShading: true });
  const dark = new THREE.MeshLambertMaterial({ color: 0x33363b, flatShading: true });
  const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.027, 0.36, 6), wood);
  handle.position.set(0, 0.1, 0);
  const head = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 0.26), metal);
  head.position.set(0, 0.28, 0);
  const strapL = new THREE.Mesh(new THREE.BoxGeometry(0.108, 0.108, 0.03), dark);
  strapL.position.set(0, 0.28, -0.08);
  const strapR = new THREE.Mesh(new THREE.BoxGeometry(0.108, 0.108, 0.03), dark);
  strapR.position.set(0, 0.28, 0.08);
  const lash = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.07, 6), dark); // lashing under the head
  lash.position.set(0, 0.2, 0);
  const butt = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.045, 6), dark); // wrapped butt end
  butt.position.set(0, -0.06, 0);
  g.add(handle, head, strapL, strapR, lash, butt);
  return g;
}

const BLUEPRINT_FILE = 'blueprint.glb'; // owner-modelled (2026-07-11); procedural sheet is the fallback
preloadGLB('/models/' + BLUEPRINT_FILE);
function buildBlueprint(): THREE.Group {
  const ready = cloneGLBIfReady('/models/' + BLUEPRINT_FILE);
  if (ready) {
    const g = new THREE.Group();
    const fitted = fitGLBHeld(ready, BLUEPRINT_FILE, 0.4);
    fitted.rotation.x = -0.3; // tilt the face toward the camera like the old sheet
    fitted.position.y = 0.12;
    g.add(fitted);
    return g;
  }
  const g = new THREE.Group();
  const paper = new THREE.MeshLambertMaterial({ color: 0x2f6fb3, flatShading: true });
  const trim = new THREE.MeshLambertMaterial({ color: 0xdfe7f2, flatShading: true });
  // An UPRIGHT sheet (face on XY toward the eye), with a paler border so the blueprint reads -- not a flat
  // board showing only its thin edge.
  const board = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.2, 0.02), paper);
  board.position.set(0, 0.16, 0);
  const inner = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.14, 0.024), trim);
  inner.position.set(0, 0.16, 0);
  g.add(board, inner);
  g.rotation.x = -0.3; // gentle tilt so the face angles toward the camera
  return g;
}

function makeMuzzleFlash(): THREE.Sprite {
  const mat = new THREE.SpriteMaterial({
    color: 0xffd070,
    transparent: true,
    opacity: 0.95,
    depthTest: false,
    blending: THREE.AdditiveBlending,
  });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(0.32, 0.32, 1);
  sprite.renderOrder = 3;
  return sprite;
}

// Dispose a group's children (geometry + material) before swapping a held model out.
function disposeChildren(parent: THREE.Object3D): void {
  for (const child of [...parent.children]) {
    parent.remove(child);
    child.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.geometry.dispose();
        const mat = m.material;
        if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
        else mat.dispose();
      }
    });
  }
}
